import React, { useEffect, useState } from 'react';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/lab';
import {
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';

import { SECTION_TYPES } from '../../../../../../../store/V2/SectionSlice';
import { getCoursesState } from '../../../../../../../store/V2/CoursesSlice';
import { useAppSelector } from '../../../../../../../hooks';
import { TPTextField } from '../../../../../../../components/Inputs';
import { ICustomSectionTemplateItem } from '../Step3';
import SubSectionTemplateItem from './SubSectionTemplateItem';
import ZoomTemplateItem from './ZoomTemplateItem';
import ResourceTemplateItem from './ResourceTemplateItem';

interface SectionTemplateItemProps {
  keyName: string;
  orderNumber: string;
  section: ICustomSectionTemplateItem;
  onDelete: (id: string) => void;
  updateAttributes: (
    id: string,
    attributes: Partial<ICustomSectionTemplateItem>
  ) => void;
}

type TState = {
  startDate: Moment | null;
  endDate: Moment | null;
};

const textFieldProps: TextFieldProps = {
  InputProps: {
    sx: { backgroundColor: '#fff' },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const containerStyles: SxProps = {
  backgroundColor: '#F8F9FD',
  borderRadius: '10px',
};

export const renderTemplateItemStatusContent = (
  section: ICustomSectionTemplateItem
): React.ReactElement | null => {
  if (section.loading) {
    return (
      <>
        <CircularProgress size={15} />
        &nbsp;
      </>
    );
  }

  if (section.error) {
    return (
      <>
        <ErrorIcon sx={{ width: '15px', height: '15px' }} color="error" />
        &nbsp;
      </>
    );
  }

  if (section.originalSectionId) {
    return (
      <>
        <CheckCircleIcon
          sx={{ width: '15px', height: '15px' }}
          color="success"
        />
        &nbsp;
      </>
    );
  }

  return null;
};

const initialState: TState = {
  startDate: null,
  endDate: null,
};

const SectionTemplateItem: React.FC<SectionTemplateItemProps> = ({
  keyName,
  orderNumber,
  section,
  onDelete,
  updateAttributes,
}) => {
  // Store
  const coursesState = useAppSelector(getCoursesState);

  // State
  const [state, setState] = useState({ ...initialState });

  const courseData = coursesState.adminCourse;
  const subSections = section.sections || [];
  const isEditable = !section.originalSectionId;

  useEffect(() => {
    setState((prevState) => {
      const newState = { ...prevState };

      if (section.startDate) {
        newState.startDate = moment(section.startDate);
      }

      if (section.endDate) {
        newState.endDate = moment(section.endDate);
      }

      if (courseData && courseData.startDate) {
        const daysDiff = Math.abs(
          moment
            .duration(moment(courseData.startDate).diff(section.dateTime))
            .asDays()
        );

        if (newState.startDate) {
          newState.startDate = newState.startDate.clone().add(daysDiff, 'days');

          if (newState.endDate) {
            const diffMins = moment
              .duration(moment(section.endDate).diff(section.startDate))
              .asMinutes();

            newState.endDate = newState.startDate
              .clone()
              .add(diffMins, 'minutes');
          }
        }
      }

      updateAttributes(section._id, {
        startDate: newState.startDate
          ? newState.startDate.clone().toISOString()
          : undefined,
        endDate: newState.endDate
          ? newState.endDate.clone().toISOString()
          : undefined,
      });

      return newState;
    });
  }, []);

  const handleStartDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        startDate: value,
      }));

      updateAttributes(section._id, {
        startDate: moment(value).toISOString(),
      });
    }
  };

  const handleEndDateChange = (value: Moment | null) => {
    if (value) {
      setState((prevState) => ({
        ...prevState,
        endDate: value,
      }));

      updateAttributes(section._id, {
        endDate: moment(value).toISOString(),
      });
    }
  };

  return (
    <>
      <Card elevation={0} sx={containerStyles}>
        <CardContent style={{ padding: '16px' }}>
          <Collapse in={Boolean(section.error)} sx={{ mb: '10px' }}>
            <Typography variant="caption" color="error">
              {section.error}
            </Typography>
          </Collapse>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" flexGrow={1} mr="15px">
              <div style={{ width: '100%' }}>
                <Typography
                  maxWidth="calc(100% - 20px)"
                  display="flex"
                  alignItems="center"
                  fontSize="16px"
                  fontWeight={600}
                  className="ellipsis-text one-line"
                >
                  {renderTemplateItemStatusContent(section)}
                  {orderNumber}. {section.title}
                </Typography>
              </div>
            </Stack>
            <Stack direction="row" alignItems="center" gap="10px">
              {section.startDate && isEditable ? (
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDateTimePicker
                    value={state.startDate}
                    inputFormat="YYYY-MM-DD hh:mm A"
                    onChange={handleStartDateChange}
                    label="Start Date & Time"
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        InputProps={{
                          ...params.InputProps,
                          ...textFieldProps.InputProps,
                        }}
                        size="small"
                        placeholder="Start Date & Time"
                      />
                    )}
                  />
                  <MobileDateTimePicker
                    value={state.endDate}
                    inputFormat="YYYY-MM-DD hh:mm A"
                    onChange={handleEndDateChange}
                    label="End Date & Time"
                    renderInput={(params) => (
                      <TPTextField
                        {...params}
                        {...textFieldProps}
                        InputProps={{
                          ...params.InputProps,
                          ...textFieldProps.InputProps,
                        }}
                        size="small"
                        placeholder="End Date & Time"
                      />
                    )}
                  />
                </LocalizationProvider>
              ) : null}
              {isEditable ? (
                <Tooltip title="Delete" placement="top">
                  <IconButton
                    color="error"
                    onClick={() => onDelete(section._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          </Stack>
          <Stack gap="10px">
            {subSections.map((subSectionItem, index) => {
              const _subSectionKeyName = `${keyName}.${index + 1}`;

              if (subSectionItem.type === SECTION_TYPES.SECTION) {
                return (
                  <Stack key={_subSectionKeyName}>
                    <SubSectionTemplateItem
                      keyName={_subSectionKeyName}
                      orderNumber={_subSectionKeyName}
                      section={subSectionItem}
                      onDelete={onDelete}
                      updateAttributes={updateAttributes}
                    />
                  </Stack>
                );
              }

              if (subSectionItem.type === SECTION_TYPES.ZOOM) {
                return (
                  <Stack key={_subSectionKeyName}>
                    <ZoomTemplateItem
                      orderNumber={_subSectionKeyName}
                      section={subSectionItem}
                      onDelete={onDelete}
                      updateAttributes={updateAttributes}
                    />
                  </Stack>
                );
              }

              if (subSectionItem.type === SECTION_TYPES.RESOURCE) {
                return (
                  <Stack key={_subSectionKeyName}>
                    <ResourceTemplateItem
                      orderNumber={_subSectionKeyName}
                      section={subSectionItem}
                      onDelete={onDelete}
                    />
                  </Stack>
                );
              }

              return null;
            })}
          </Stack>
        </CardContent>
      </Card>
    </>
  );
};

export default SectionTemplateItem;
